<template>
  <div :class="[styles.inputFieldPos, stateClass]" :style="containerStyle">
    <label v-if="showTitle" :class="styles.label"> {{ title }}</label>
    <div
      :class="styles.textBoxPos"
      :style="borderStyle"
      @mouseenter="setHover"
      @mouseleave="removeHover"
      @click="setFocus"
      @focusout="removeFocus"
    >
      <input
        ref="inputRef"
        :type="inputType"
        :class="styles.text"
        :value="textInput"
        @input="updateValue"
        @blur="validateInput"
        :placeholder="$t('message.enter_text')"
        :style="styles.textWrapper"
      />
      <img
        v-if="showIcon"
        :class="[styles.keyboardArrowDownIcon, isFocused ? styles.rotateIcon : '']"
        alt=""
        src="@/assets/keyboard_arrow_down.svg"
      />
    </div>
    <div v-show="isError" :class="styles.errorMessage">{{ $t(errorLabel) }}</div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, type PropType } from 'vue'
import { useLanguageStore } from '../../stores/language'
import { useI18n } from 'vue-i18n'
import styles from './InputField.module.css'
import { InputFieldType } from './InputFieldType'
import { ValidateInputField } from '@/Utils/customValidators'

export default defineComponent({
  name: 'InputField',
  props: {
    borderColor: { type: String, default: '#000' },
    inputFieldTitle: { type: String, default: '' },
    inputFieldType: { type: Number as PropType<InputFieldType>, default: InputFieldType.Default },
    showIcon: { type: Boolean, default: false },
    modelValue: { type: String, default: '' },
    width: { tupe: String, default: '' },
    validateOnCreate: { type: Boolean, default: false },    
    externalValidationResult: { type: Boolean, default: true },
    externalValidationErrorLabel: { type: String, default: 'message.field_required' },
    required: { type: Boolean, default: true }
  },

  setup(props, { emit }) {
    const { t } = useI18n()
    const languageStore = useLanguageStore()
    const showTitle = ref(props.inputFieldTitle.length > 0)
    const inputRef = ref<HTMLInputElement | null>(null)

    const titleLocalizedKey = ref(props.inputFieldTitle)
    const title = computed(() => t(titleLocalizedKey.value))

    const isFocused = ref(false)
    const isHovered = ref(false)
    const isError = ref(false)
    const errorLabel = ref('message.field_required')

    const borderStyle = computed(() => {
      if (!isHovered.value && !isFocused.value && !isError.value) {
        return { borderColor: props.borderColor }
      }
      return {}
    })

    const containerStyle = computed(() => {
      if (props.width) {
        return {width: props.width};
      }
      return {}
    });

    const textInput = ref(props.modelValue)

    const inputType = computed(() => {
      if (props.inputFieldType === InputFieldType.Number) return "number";
      return "text";
    })

    const stateClass = computed(() => {
      if (isError.value) return styles.error
      if (isFocused.value) return styles.focus
      if (isHovered.value) return styles.hover
      return ''
    })
    const focusInput = () => {
      inputRef.value?.focus()
    }
    function setFocus() {
      inputRef.value?.focus()
      isFocused.value = true
      isError.value = false
    }

    function removeFocus() {
      isFocused.value = false
    }
    function validateInput() {
      if (!props.externalValidationResult) {
        isError.value = true;
        errorLabel.value = props.externalValidationErrorLabel;
        return;
      }
      const validationResult = ValidateInputField(textInput.value, props.inputFieldType, props.required);
      if (!validationResult.isValid) {
        isError.value = true;
        errorLabel.value = validationResult.errorLabel
      };
    }

    const isEmpty = (value: string): boolean => {
      if (!props.required) return false;
      return value === ''
    }
    const validateEmail = (email: string): boolean => {
      const re =
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\\.,;:\s@"]+\.)+[^<>()[\]\\.,;:\s@"]{2,})$/i
      return re.test(email)
    }

    const validatePassword = (password: string): boolean => {
      return password.length >= 8
    }
    function setHover() {
      isHovered.value = true
    }

    function removeHover() {
      isHovered.value = false
    }
    /*function updateErrorLanguageKey(key: string) {
      errorLabel.value = key
    }*/
    function updateTitleLanguageKey(key: string) {
      titleLocalizedKey.value = key
    }
    languageStore.$subscribe((mutation, state) => {
      //updateErrorLanguageKey('message.field_required')
      updateTitleLanguageKey(titleLocalizedKey.value)
    })

    watch(
      () => props.modelValue,
      (newValue) => {
        textInput.value = newValue
      }
    )
    watch(
      () => props.externalValidationResult,
      () => {
        console.log('externalValidationResult watcher');
        validateInput();
      }
    );
    const updateValue = (event: Event) => {
      const input = event.target as HTMLInputElement
      emit('update:modelValue', input.value)
    }

    if (props.validateOnCreate) validateInput();

    return {
      updateValue,
      borderStyle,
      containerStyle,
      showTitle,
      textInput,
      title,
      //errorLabel,
      isFocused,
      isError,
      stateClass,
      setFocus,
      removeFocus,
      validateInput,
      setHover,
      removeHover,
      styles,
      inputRef,
      focusInput,
      inputType,
      errorLabel
    }
  }
})
</script>
